import React, { ChangeEvent, useEffect, useState } from 'react';

import { Input, Modal } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import {
  validateEmailFormat,
  validateNumberAndHyphenOnly,
  validateTelFormat,
  validateVenderManagerName,
} from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type { TypeInputState } from '~/types/common.types';
import type {
  TypeManagerRowInfo,
  TypeOwnerSupplierManager,
  TypeOwnerSupplierManagerWithIdAndCode,
} from '~/types/vender.types';

import styles from './VenderSupplierManagerModifyModal.module.scss';

// 타입 가드 함수
const isTypeOwnerSupplierManagerWithIdAndCode = (
  manager: TypeOwnerSupplierManager
): manager is TypeOwnerSupplierManagerWithIdAndCode =>
  'ownerSupplierManagerId' in manager && 'ownerSupplierCode' in manager;

const isTypeManagerRowInfo = (manager: TypeOwnerSupplierManager): manager is TypeManagerRowInfo =>
  'rowIndex' in manager;

type TypeVenderSupplierManagerModifyModalProps = {
  open: boolean;
  managerInfo: TypeManagerRowInfo | TypeOwnerSupplierManagerWithIdAndCode | null;
  onCancel: () => void;
  onConfirm: (managerInfo: TypeManagerRowInfo | TypeOwnerSupplierManagerWithIdAndCode) => void;
};

const DEFAULT_MANAGER_MODIFY_FORM = {
  name: '',
  position: '',
  tel: '',
  email: '',
};

/**
 * [화주사 정보 관리 > 공급사 관리] 담당자 수정 모달
 */
const VenderSupplierManagerModifyModal = ({
  open,
  managerInfo,
  onCancel,
  onConfirm,
}: TypeVenderSupplierManagerModifyModalProps) => {
  const [managerModifyForm, setManagerModifyForm] = useState(DEFAULT_MANAGER_MODIFY_FORM);

  const [managerNameStatus, setManagerNameStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [managerTelStatus, setManagerTelStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [managerEmailStatus, setManagerEmailStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  useEffect(() => {
    if (open && managerInfo) {
      setManagerModifyForm({
        name: managerInfo.ownerSupplierManagerName,
        position: managerInfo.ownerSupplierManagerPosition,
        tel: managerInfo.ownerSupplierManagerTel,
        email: managerInfo.ownerSupplierManagerEmail,
      });
    }
  }, [open]);

  const resetAllInputStatus = () => {
    setManagerModifyForm(DEFAULT_MANAGER_MODIFY_FORM);

    setManagerNameStatus(DEFAULT_INPUT_STATUS);
    setManagerTelStatus(DEFAULT_INPUT_STATUS);
    setManagerEmailStatus(DEFAULT_INPUT_STATUS);
  };

  const handleFocusManagerName = () => setManagerNameStatus(DEFAULT_INPUT_STATUS);

  const handleFocusManagerTel = () => setManagerTelStatus(DEFAULT_INPUT_STATUS);

  const handleFocusManagerEmail = () => setManagerEmailStatus(DEFAULT_INPUT_STATUS);

  const handleChangeManagerName = (e: ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, '');
    setManagerModifyForm({ ...managerModifyForm, name: valueWithoutSpaces });
  };

  const handleChangeManagerPosition = (e: ChangeEvent<HTMLInputElement>) =>
    setManagerModifyForm({ ...managerModifyForm, position: e.target.value });

  const handleChangeManagerTel = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (validateNumberAndHyphenOnly(updatedValue)) {
      setManagerModifyForm({ ...managerModifyForm, tel: updatedValue });
    }
  };

  const handleChangeManagerEmail = (e: ChangeEvent<HTMLInputElement>) =>
    setManagerModifyForm({ ...managerModifyForm, email: e.target.value });

  const handleCancel = () => {
    resetAllInputStatus();
    onCancel();
  };

  const validateForm = () => {
    let isValidForm = true;

    // 담당자명 유효성 체크
    if (managerModifyForm.name === '') {
      setManagerNameStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateVenderManagerName(managerModifyForm.name)) {
      setManagerNameStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    // 휴대폰번호 유효성 체크
    if (managerModifyForm.tel === '') {
      setManagerTelStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateTelFormat(managerModifyForm.tel)) {
      setManagerTelStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    // 이메일 유효성 체크
    if (managerModifyForm.email.length > 0 && !validateEmailFormat(managerModifyForm.email)) {
      setManagerEmailStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    return isValidForm;
  };

  const handleConfirm = () => {
    const isValidForm = validateForm();

    if (isValidForm) {
      let manager: TypeOwnerSupplierManagerWithIdAndCode | TypeManagerRowInfo;

      if (managerInfo && isTypeOwnerSupplierManagerWithIdAndCode(managerInfo)) {
        manager = {
          ownerSupplierManagerId: managerInfo.ownerSupplierManagerId,
          ownerSupplierManagerName: managerModifyForm.name,
          ownerSupplierManagerPosition: managerModifyForm.position.trim(),
          ownerSupplierManagerTel: managerModifyForm.tel,
          ownerSupplierManagerEmail: managerModifyForm.email,
        } as TypeOwnerSupplierManagerWithIdAndCode;
      } else if (managerInfo && isTypeManagerRowInfo(managerInfo)) {
        manager = {
          ownerSupplierManagerName: managerModifyForm.name,
          ownerSupplierManagerPosition: managerModifyForm.position.trim(),
          ownerSupplierManagerTel: managerModifyForm.tel,
          ownerSupplierManagerEmail: managerModifyForm.email,
          rowIndex: managerInfo.rowIndex,
        } as TypeManagerRowInfo;
      } else {
        return;
      }

      onConfirm(manager);
    }
  };

  return (
    <Modal open={open} title="담당자 정보 수정" confirmText="저장" onCancel={handleCancel} onConfirm={handleConfirm}>
      <div className={styles['modal-contents-container']}>
        <FormField
          label="담당자명"
          required
          contents={
            <Input
              type="text"
              size="small"
              placeholder="입력"
              status={managerNameStatus.status}
              validationMessage={managerNameStatus.message}
              value={managerModifyForm.name}
              onFocus={handleFocusManagerName}
              onChange={handleChangeManagerName}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="직위"
          contents={
            <Input
              type="text"
              size="small"
              placeholder="입력"
              value={managerModifyForm.position}
              onChange={handleChangeManagerPosition}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="휴대폰번호"
          required
          contents={
            <Input
              type="text"
              size="small"
              placeholder="- 포함 입력"
              status={managerTelStatus.status}
              validationMessage={managerTelStatus.message}
              value={managerModifyForm.tel}
              onFocus={handleFocusManagerTel}
              onChange={handleChangeManagerTel}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="이메일"
          contents={
            <Input
              type="text"
              size="small"
              placeholder="example@kls.com"
              status={managerEmailStatus.status}
              validationMessage={managerEmailStatus.message}
              value={managerModifyForm.email}
              onFocus={handleFocusManagerEmail}
              onChange={handleChangeManagerEmail}
            />
          }
          labelClassName={styles['input-row-label']}
        />
      </div>
    </Modal>
  );
};

export default VenderSupplierManagerModifyModal;
